<div class="home">
  <h1 class="sr-only">TheNearByShop - shop from local stores near you</h1>

  <!-- ================= PROMOTIONS (from /banners API) ================= -->
  @if (bannersLoading()) {
    <app-loading-skeleton variant="banner"></app-loading-skeleton>
  } @else if (banners().length) {
    <section class="banner-carousel" aria-roledescription="carousel" aria-label="Offers and promotions"
      (mouseenter)="pauseBanners(true)" (mouseleave)="pauseBanners(false)"
      (focusin)="pauseBanners(true)" (focusout)="pauseBanners(false)">
      <div class="banner-track" #bannerTrack (scroll)="onBannerScroll(bannerTrack)">
        @for (banner of banners(); track banner.id; let i = $index) {
          <div class="banner-slide" role="group" aria-roledescription="slide" [attr.aria-label]="(i + 1) + ' of ' + banners().length">
            @if (banner.link && !banner.external) {
              <a [routerLink]="banner.link" (click)="onBannerClick(banner)" [attr.aria-label]="banner.title">
                <img [src]="banner.image" [alt]="banner.title" width="1280" height="480"
                  [attr.loading]="i === 0 ? 'eager' : 'lazy'" [attr.fetchpriority]="i === 0 ? 'high' : null" decoding="async" />
              </a>
            } @else if (banner.link) {
              <a [href]="banner.link" target="_blank" rel="noopener" (click)="onBannerClick(banner)" [attr.aria-label]="banner.title">
                <img [src]="banner.image" [alt]="banner.title" width="1280" height="480"
                  [attr.loading]="i === 0 ? 'eager' : 'lazy'" decoding="async" />
              </a>
            } @else {
              <img [src]="banner.image" [alt]="banner.title" width="1280" height="480"
                [attr.loading]="i === 0 ? 'eager' : 'lazy'" [attr.fetchpriority]="i === 0 ? 'high' : null" decoding="async" />
            }
          </div>
        }
      </div>

      @if (banners().length > 1) {
        <div class="banner-dots" role="tablist" aria-label="Choose slide">
          @for (banner of banners(); track banner.id; let i = $index) {
            <button type="button" role="tab" [class.is-active]="activeBanner() === i" [attr.aria-selected]="activeBanner() === i"
              [attr.aria-label]="'Show slide ' + (i + 1)" (click)="goToBanner(i)"></button>
          }
        </div>
      }
    </section>
  } @else {
    <!-- No banners configured: a compact brand intro instead of an empty gap -->
    <section class="intro-hero">
      <div>
        <h2>Your neighbourhood shops, <span>delivered.</span></h2>
        <p>Order groceries and daily essentials from trusted local stores, subscribe to regular deliveries, or book services near you.</p>
      </div>
      <div class="intro-actions">
        <a class="ds-btn ds-btn--primary" routerLink="/categories">Shop categories</a>
        <a class="ds-btn ds-btn--outline" routerLink="/stores-view-all">Browse shops</a>
      </div>
    </section>
  }


  <!-- ================= CATEGORIES ================= -->
  <section class="home-section" aria-labelledby="cat-heading">
    <div class="section-title">
      <h2 id="cat-heading">Shop by category</h2>
      @if (categories().length > 8) {
        <a class="section-link" routerLink="/categories">See all <i class="fas fa-arrow-right" aria-hidden="true"></i></a>
      }
    </div>

    @if (categoriesLoading()) {
      <app-loading-skeleton variant="category-tiles" [count]="8"></app-loading-skeleton>
    } @else if (categories().length) {
      <ul class="category-tiles" role="list">
        @if (!hasPrintCategory()) {
          <li>
            <a class="category-tile" routerLink="/print">
              <span class="tile-img tile-icon" aria-hidden="true"><i class="fas fa-print"></i></span>
              <span class="tile-name">Printing &amp; Photocopy</span>
            </a>
          </li>
        }
        @for (cat of categories().slice(0, 16); track cat.id; let i = $index) {
          <li>
            <a class="category-tile" [routerLink]="categoryLink(cat)" (click)="trackCategory(cat)">
              <span class="tile-img">
                <img [src]="cat.image || 'assets/images/no-image.jpg'" alt="" width="80" height="80" [attr.loading]="i < 8 ? 'eager' : 'lazy'" decoding="async" />
              </span>
              <span class="tile-name">{{ cat.name }}</span>
            </a>
          </li>
        }
      </ul>
    }
  </section>

  <!-- ================= RECENT ORDERS / ORDER AGAIN ================= -->
  @if (recentOrdersLoading() && !recentOrders().length) {
    <section class="home-section" aria-label="Loading your recent orders">
      <div class="section-title"><h2>Your recent orders</h2></div>
      <app-loading-skeleton variant="list" [count]="2"></app-loading-skeleton>
    </section>
  } @else if (recentOrders().length) {
    <section class="home-section" aria-labelledby="orders-heading">
      <div class="section-title">
        <h2 id="orders-heading">Your recent orders</h2>
        <a class="section-link" [routerLink]="['/profile']" [queryParams]="{ tab: 'orders' }">All orders <i class="fas fa-arrow-right" aria-hidden="true"></i></a>
      </div>
      <div class="order-cards">
        @for (order of recentOrders(); track order.id) {
          <article class="order-card" [class.is-live]="isActive(order.status)">
            <header class="order-card__top">
              <div class="order-shop">
                <strong>{{ shopName(order) || 'Order ' + orderKey(order) }}</strong>
                <time [attr.datetime]="order.created_at">{{ order.created_at | date:'d MMM, h:mm a' }}</time>
              </div>
              <span class="ds-badge" [ngClass]="'ds-badge--' + statusTone(order.status)">
                <i class="fas" [ngClass]="statusMeta(order.status).icon" aria-hidden="true"></i>
                {{ statusMeta(order.status).label }}
              </span>
            </header>

            @if (order.items.length) {
              <ul class="order-thumbs" role="list" [attr.aria-label]="orderItemsLabel(order)">
                @for (item of order.items.slice(0, 4); track item.id) {
                  <li>
                    <img [src]="item.product_image || 'assets/images/default-product.avif'" [alt]="item.product_name || ''" width="44" height="44" loading="lazy" />
                    @if (item.quantity > 1) { <span class="qty">×{{ item.quantity }}</span> }
                  </li>
                }
                @if (order.items.length > 4) {
                  <li class="more">+{{ order.items.length - 4 }}</li>
                }
              </ul>
            }
            <p class="order-items">{{ orderItemsLabel(order) }}</p>

            @if (isActive(order.status)) {
              <app-order-timeline [order]="order" layout="compact"></app-order-timeline>
              <p class="order-live-note">
                {{ statusMeta(order.status).description }}
                @if (eta(order); as etaAt) { · Arriving by {{ etaAt | date:'h:mm a' }} }
                @if (rider(order); as r) { · {{ r.name }} }
              </p>
            }

            <dl class="order-facts">
              <div><dt>Total</dt><dd>₹{{ (order.grand_total || order.total_price) | number:'1.0-2' }}</dd></div>
              @if (itemCount(order)) { <div><dt>Items</dt><dd>{{ itemCount(order) }}</dd></div> }
              @if (paymentLabel(order.payment_method)) { <div><dt>Payment</dt><dd>{{ paymentLabel(order.payment_method) }}</dd></div> }
            </dl>

            <footer class="order-actions">
              <a class="ds-btn ds-btn--outline ds-btn--sm" [routerLink]="['/order-summary', orderKey(order)]">
                {{ isActive(order.status) ? 'Track order' : 'View details' }}
              </a>
              @if (canReorder(order) && !isActive(order.status)) {
                <button type="button" class="ds-btn ds-btn--accent ds-btn--sm" (click)="reorder(order)" [disabled]="reorderingId() !== null">
                  @if (reorderingId() === order.id) {
                    <i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Adding…
                  } @else {
                    <i class="fas fa-rotate-right" aria-hidden="true"></i> Order again
                  }
                </button>
              }
            </footer>
          </article>
        }
      </div>
    </section>
  }

  <!-- ================= RECENTLY VIEWED (this device) ================= -->
  @if (recentlyViewed().length) {
    <app-product-rail title="Continue shopping" icon="fa-clock-rotate-left" [products]="recentlyViewed()"></app-product-rail>
  }

  <!-- ================= SHOPS NEAR YOU ================= -->
  <app-shop-section title="Shops near you" icon="fa-store" [shops]="productShops()" [loading]="loadingStores()"
    viewAllLink="/stores-view-all" (quickView)="openShopQuickView($event)">
    <div empty class="no-shops">
      <i class="fas fa-map-location-dot no-shops-icon" aria-hidden="true"></i>
      <h3>No shops deliver here yet</h3>
      <p>Try a larger search radius or a different address, or tell us where you'd like us next.</p>
      <div class="no-shops-actions">
        <button type="button" class="ds-btn ds-btn--primary" (click)="openLocationPicker()">Change location</button>
        <button type="button" class="ds-btn ds-btn--outline" (click)="openWhatsAppNotify()">
          <i class="fab fa-whatsapp" aria-hidden="true"></i> Request my area
        </button>
      </div>
    </div>
  </app-shop-section>

  @if (featuredShops().length) {
    <app-shop-section title="Featured shops" icon="fa-crown" [shops]="featuredShops()" (quickView)="openShopQuickView($event)">
    </app-shop-section>
  }

  <!-- ================= CATEGORY SHELVES (lazy, from products API) ================= -->
  @for (shelf of shelves(); track shelf.category.id) {
    @if (showShelf(shelf)) {
      <section class="home-section shelf" [attr.aria-label]="shelf.category.name">
        <div class="section-title">
          <h2>{{ shelf.category.name }}</h2>
          @if (shelf.products.length) {
            <a class="section-link" [routerLink]="['/category', shelf.category.slug]">See all <i class="fas fa-arrow-right" aria-hidden="true"></i></a>
          }
        </div>
        <app-product-rail [title]="''" [products]="shelf.products" [loading]="shelf.loading" [skeletonCount]="6"></app-product-rail>
      </section>
    }
  }
  <div #shelfSentinel class="shelf-sentinel" aria-hidden="true"></div>

  <!-- ================= SERVICES ================= -->
  @if (serviceProviderShops().length) {
    <app-shop-section title="Book services near you" icon="fa-calendar-check" [shops]="serviceProviderShops()"
      (quickView)="openShopQuickView($event)">
    </app-shop-section>
  }

  <!-- ================= HOW IT WORKS (product capabilities, no performance claims) ================= -->
  <section class="value-strip" aria-label="Why TheNearByShop">
    <div class="value-item">
      <i class="fas fa-store" aria-hidden="true"></i>
      <div><strong>Real local shops</strong><span>Buy from stores in your own neighbourhood</span></div>
    </div>
    <div class="value-item">
      <i class="fas fa-rotate" aria-hidden="true"></i>
      <div><strong>Subscriptions</strong><span>Daily, weekly or monthly essentials on schedule</span></div>
    </div>
    <div class="value-item">
      <i class="fas fa-calendar-check" aria-hidden="true"></i>
      <div><strong>Book services</strong><span>Reserve slots with local service providers</span></div>
    </div>
    <div class="value-item">
      <i class="fas fa-shield-halved" aria-hidden="true"></i>
      <div><strong>Secure checkout</strong><span>Pay online via Razorpay or cash on delivery</span></div>
    </div>
  </section>

  <!-- ================= LOCATION PROMPT =================
       Floating (position: fixed) because it can only be decided in the browser:
       inserting it into the page flow after hydration would shift everything below (CLS). -->
  @if (isBrowser && !currentLocation() && !locationPromptDismissed()) {
    <section class="location-prompt" aria-label="Set delivery location">
      <i class="fas fa-location-dot" aria-hidden="true"></i>
      <div>
        <strong>See what's available near you</strong>
        <span>Set your delivery location for nearby shops and delivery availability.</span>
      </div>
      <button type="button" class="ds-btn ds-btn--primary ds-btn--sm" (click)="openLocationPicker()">Set location</button>
      <button type="button" class="prompt-close" (click)="locationPromptDismissed.set(true)" aria-label="Dismiss">
        <i class="fas fa-xmark" aria-hidden="true"></i>
      </button>
    </section>
  }
</div>
